/**
 * The endpoints the SPA watches (PLAN §10.2's multi-connection model), from
 * the runtime config: the backend that served the page, plus `/config.json`'s
 * `status` block — the stage's cloud and the site's nodes. Each once, keyed by
 * its absolute API URL; relative URLs resolve against the page's origin.
 *
 * A node's WS endpoint is its API URL + `/ws` (the state service's layout;
 * behind the Vite proxy `/api` → `/api/ws`), so the status block names only
 * `apiUrl` for nodes.
 *
 * A config without a `status` block (written before it existed) watches the
 * backend alone.
 */
import type { RuntimeConfigWire } from '@acp/api-contract/runtimeConfig.js';
import type { StatusResponseWire } from '@acp/api-contract/statusRoutes.js';
import { resolveEndpoints } from '../lib/runtimeConfig.js';

export type EndpointKind = 'cloud' | 'node';

export type Endpoint = {
  /** The absolute API URL: unique per endpoint. */
  readonly id: string;
  readonly kind: EndpointKind;
  readonly label: string;
  readonly apiUrl: string;
  readonly wsUrl: string;
  /** Whether this endpoint served the page (the runtime config's `backend`). */
  readonly servesPage: boolean;
  /**
   * Whether it may be the primary backend (carry the data path). Two things
   * must hold:
   *   - it accepts this page's token: with Cognito every endpoint does (§11), but a
   *     development node's token is its own (a key that node made), and a 401 elsewhere
   *     would sign the operator out;
   *   - the browser lets this page call it (wave 4b-1, D16): a node answers CORS for the
   *     cloud SPA's origin and its site's HTTPS names (with the Private Network Access
   *     preflight), so from any page every node is callable; the cloud API answers CORS
   *     itself (wave 4c, apps/cloud-api/src/cors.ts) for the SPA's domain, the dev ports
   *     and the nodes' and sites' HTTPS names (`callableCloud`) — so a node page opened by
   *     its HTTPS name fails over to the cloud too; one opened by IP (or over plain HTTP)
   *     watches the cloud for the status column but never fails over to it. And a node is callable only over
   *     HTTPS from an HTTPS page (`callableNode`): its CORS admits the cloud SPA and the
   *     site's HTTPS names, never a plain-HTTP origin, and an HTTPS page may not call
   *     plain HTTP (mixed content) — a peer configured as `http://<LAN IP>` answers its
   *     CORS-open `/health` probe but not one data call, so it is watched, never primary.
   */
  readonly canBePrimary: boolean;
};

/** Whether a page at `origin` can call a node at `apiUrl` it was not served by: both HTTPS (the doc comment above). */
export function callableNode(apiUrl: string, origin: string): boolean {
  return apiUrl.startsWith('https://') && origin.startsWith('https://');
}

/**
 * Whether a page at `origin`, served by a node, can call the cloud API: the cloud's CORS admits a node's or a
 * site's local HTTPS name (D16: `https://n-…`/`https://s-…` under the stage's node domain; the SPA does not know
 * the domain, the cloud checks it strictly).
 */
export function callableCloud(origin: string): boolean {
  // No port: the cloud's allowlist (`nodeOriginPattern`) admits the default HTTPS port only (review finding).
  return /^https:\/\/[ns]-[a-z0-9]+\.[^:/]+$/.test(origin);
}

function nodeWsPath(apiUrl: string): string {
  return `${apiUrl === '' ? '' : apiUrl.replace(/\/$/, '')}/ws`;
}

/** Every watched endpoint, in preference order within each kind: nodes as listed (the serving node first), then the cloud. */
export function watchedEndpoints(config: RuntimeConfigWire, origin: string): Endpoint[] {
  const own = resolveEndpoints(config.backend, origin);
  const out: Endpoint[] = [];
  const add = (kind: EndpointKind, label: string, apiUrl: string, wsUrl: string): void => {
    const resolved = resolveEndpoints({ kind, label, apiUrl, wsUrl }, origin);
    if (out.some((e) => e.id === resolved.apiUrl)) return;
    const servesPage = resolved.apiUrl === own.apiUrl;
    out.push({ id: resolved.apiUrl, kind, label, apiUrl: resolved.apiUrl, wsUrl: resolved.wsUrl, servesPage, canBePrimary: servesPage || (config.auth.kind === 'cognito' && (kind === 'node' ? callableNode(resolved.apiUrl, origin) : config.backend.kind === 'cloud' || callableCloud(origin))) });
  };
  const status = config.status;
  // The serving backend is always watched, and a serving node is listed first among the nodes.
  if (config.backend.kind === 'node') add('node', config.backend.label, config.backend.apiUrl, config.backend.wsUrl);
  for (const node of status?.nodes ?? []) add('node', node.label, node.apiUrl, nodeWsPath(node.apiUrl));
  const cloud = status?.cloud ?? null;
  if (config.backend.kind === 'cloud') add('cloud', config.backend.label, config.backend.apiUrl, config.backend.wsUrl);
  else if (cloud !== null) add('cloud', 'Cloud', cloud.apiUrl, cloud.wsUrl);
  return out;
}

/**
 * The nodes the cloud's view names (`GET /status`, wave 4b-1): each one with a
 * local HTTPS name (D16) as a node endpoint to watch — how a page served by
 * the cloud learns the tenant's nodes (CloudFront's `/config.json` is the
 * same for every tenant, so it cannot list them). Ids already watched are
 * left out.
 */
export function statusEndpoints(view: StatusResponseWire, config: RuntimeConfigWire, origin: string, known: ReadonlySet<string>): Endpoint[] {
  const out: Endpoint[] = [];
  for (const site of view.sites) {
    for (const node of site.nodes) {
      const apiUrl = node.apiUrl;
      if (apiUrl === null || known.has(apiUrl) || out.some((e) => e.id === apiUrl)) continue;
      out.push({ id: apiUrl, kind: 'node', label: node.label === '' ? node.nodeId : node.label, apiUrl, wsUrl: nodeWsPath(apiUrl).replace(/^http/, 'ws'), servesPage: false, canBePrimary: config.auth.kind === 'cognito' && callableNode(apiUrl, origin) });
    }
  }
  return out;
}
